import * as Sentry from "@sentry/angular";
import type { BrowserOptions } from "@sentry/angular";

interface SentryEnvironment {
  production: boolean;
  dsn: string;
  env: string;
}

type BeforeSendSpan = NonNullable<BrowserOptions["beforeSendSpan"]>;
type BeforeSendTransaction = NonNullable<
  BrowserOptions["beforeSendTransaction"]
>;
type TracesSampler = NonNullable<BrowserOptions["tracesSampler"]>;

const URL_IN_DESCRIPTION = /(?:^|\s)(?:[a-z][a-z\d+.-]*:\/\/|\.{0,2}\/)/i;
const URL_DATA_KEY = /(^|[._-])url($|[._-])/i;
const HTTP_URL = /https?:\/{1,2}/i;
const USER_DATA_KEYS = new Set(["user", "user.id", "user.email"]);
const DEFAULT_PERFORMANCE_SAMPLE_RATE = 0.005;
const CRITICAL_PERFORMANCE_SAMPLE_RATE = 0.01;
const INVALID_ROUTE_TRANSACTION_NAME = "<invalid-browser-route>";
const LOW_VALUE_PERFORMANCE_SPAN_OPERATIONS = new Set([
  "mark",
  "measure",
  "paint",
]);
// Retain extra coverage for entry, dashboard, and onboarding bank-linking journeys.
const CRITICAL_ROUTE_PREFIXES = [
  "/",
  "/login",
  "/onboarding-intro",
  "/landing",
  "/main/dashboard",
  "/bank-statements",
  "/finverse",
  "/link-bank-account",
  "/smile",
] as const;

function stripUrlQueryAndFragment(value: string): string {
  const suffixStart = value.search(/[?#]/);

  return suffixStart === -1 ? value : value.slice(0, suffixStart);
}

function normalizeRouteTransactionName(value: string): string {
  let routeName = stripUrlQueryAndFragment(value.trim());
  const absoluteUrlStart = routeName.search(HTTP_URL);

  if (absoluteUrlStart > 0) {
    return INVALID_ROUTE_TRANSACTION_NAME;
  } else if (absoluteUrlStart === 0) {
    try {
      routeName = new URL(routeName).pathname;
    } catch {
      return value;
    }
  }

  if (!routeName.startsWith("/")) {
    return value;
  }

  return routeName === "/" ? routeName : routeName.replace(/\/+$/, "");
}

function isCriticalRoute(routeName: string): boolean {
  const normalizedRouteName = normalizeRouteTransactionName(routeName);

  return CRITICAL_ROUTE_PREFIXES.some((prefix) =>
    prefix === "/"
      ? normalizedRouteName === prefix
      : normalizedRouteName === prefix ||
        normalizedRouteName.startsWith(`${prefix}/`)
  );
}

const samplePerformanceTrace: TracesSampler = ({ name, parentSampled }) => {
  if (parentSampled !== undefined) {
    return parentSampled;
  }

  return isCriticalRoute(name)
    ? CRITICAL_PERFORMANCE_SAMPLE_RATE
    : DEFAULT_PERFORMANCE_SAMPLE_RATE;
};

function sanitizeUrlData(
  source: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!source) {
    return source;
  }

  const data = { ...source };

  for (const key of Object.keys(data)) {
    const value = data[key];

    if (USER_DATA_KEYS.has(key)) {
      delete data[key];
    } else if (
      typeof value === "string" &&
      (URL_DATA_KEY.test(key) || key === "http.target")
    ) {
      data[key] = stripUrlQueryAndFragment(value);
    }
  }

  return data;
}

function sanitizeRequestHeaders(
  source: Record<string, string> | undefined
): Record<string, string> | undefined {
  if (!source) {
    return source;
  }

  const headers = { ...source };

  for (const key of Object.keys(headers)) {
    if (/^referr?er$/i.test(key)) {
      delete headers[key];
    }
  }

  return headers;
}

/** Identifies high-volume browser spans that do not help diagnose FINN flows. */
function isLowValuePerformanceSpan(operation: string | undefined): boolean {
  return Boolean(
    operation &&
    (operation.startsWith("resource.") ||
      operation.startsWith("browser.") ||
      LOW_VALUE_PERFORMANCE_SPAN_OPERATIONS.has(operation))
  );
}

function createPerformanceSpanSanitizer(appPlatform: string): BeforeSendSpan {
  return (span) => {
    const descriptionContainsUrl =
      span.op === "ui.angular.routing" ||
      (typeof span.description === "string" &&
        URL_IN_DESCRIPTION.test(span.description));

    return {
      ...span,
      description:
        descriptionContainsUrl && typeof span.description === "string"
          ? stripUrlQueryAndFragment(span.description)
          : span.description,
      data: {
        ...sanitizeUrlData(span.data),
        app_platform: appPlatform,
      },
    };
  };
}

const sanitizePerformanceTransaction: BeforeSendTransaction = (event) => {
  const trace = event.contexts?.trace;

  return {
    ...event,
    spans: event.spans?.filter((span) => !isLowValuePerformanceSpan(span.op)),
    transaction:
      typeof event.transaction === "string"
        ? normalizeRouteTransactionName(event.transaction)
        : event.transaction,
    user: undefined,
    breadcrumbs: undefined,
    request: event.request
      ? {
          ...event.request,
          url:
            typeof event.request.url === "string"
              ? stripUrlQueryAndFragment(event.request.url)
              : event.request.url,
          query_string: undefined,
          headers: sanitizeRequestHeaders(event.request.headers),
        }
      : event.request,
    contexts: trace
      ? {
          ...event.contexts,
          trace: {
            ...trace,
            data: sanitizeUrlData(trace.data),
          },
        }
      : event.contexts,
  };
};

export function initializeSentryMonitoring(
  environment: SentryEnvironment,
  release: string,
  appPlatform: string
): void {
  if (!environment.production) {
    return;
  }

  Sentry.init({
    // Angular routing span descriptions can contain login and bank callback
    // query values. Remove URL suffixes before performance data leaves FINN.
    beforeSendSpan: createPerformanceSpanSanitizer(appPlatform),
    beforeSendTransaction: sanitizePerformanceTransaction,
    dsn: environment.dsn,
    environment: environment.env,
    ignoreErrors: ["Java object is gone", "overlay does not exist"],
    integrations: [
      Sentry.replayIntegration({
        maskAllInputs: true,
        blockAllMedia: true,
        maskAllText: true,
      }),
      Sentry.httpClientIntegration(),
      Sentry.browserTracingIntegration({
        // FE-2652 only adds real-user browser performance collection.
        // Keep distributed request tracing outside this change's scope.
        traceFetch: false,
        traceXHR: false,
        enableLongTask: false,
        enableLongAnimationFrame: false,
      }),
    ],
    tracesSampler: samplePerformanceTrace,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.01,
    sendDefaultPii: false,
    release,
    initialScope: {
      tags: {
        app_platform: appPlatform,
      },
    },
  });
}
